import {DONATION_ERROR, checkDonationError} from '@wix/wix-events-commons-statics'
import {useTranslation} from '@wix/yoshi-flow-editor'
import React, {useCallback, useEffect} from 'react'
import {usePrevious} from '../../../../../../../../commons/hooks/use-previous'
import {useAppActions, useAppSelector} from '../../../../../hooks/runtime'
import {getPlaceDonationError} from '../../../../../selectors/seating/errors'
import {isInAccessibilityMode, isInDescription, isInMobileBasket} from '../../../../../selectors/seating/mode'
import {isPlaceReservableAsWhole} from '../../../../../selectors/seating/place'
import {DonationInput} from '../../../../donation-input/donation-input'
import {TicketDonationProps} from './interfaces'
import {classes} from './ticket-donation.st.css'

export const TicketDonation = ({place, describedBy}: TicketDonationProps) => {
  const {t} = useTranslation()
  const {addPlaceDonation, setPlaceDonationError} = useAppActions()

  const {ticket, donation} = place
  const wholeTable = isPlaceReservableAsWhole(place)

  const error = useAppSelector(state => getPlaceDonationError(state, place.id))
  const showAccessibilityMode = useAppSelector(isInAccessibilityMode)
  const inDescription = useAppSelector(isInDescription)
  const inMobileBasket = useAppSelector(isInMobileBasket)

  const previousShowAccessibilityMode = usePrevious(showAccessibilityMode)

  useEffect(() => {
    if (previousShowAccessibilityMode !== showAccessibilityMode) {
      if (error === DONATION_ERROR.EMPTY_DONATION) {
        setPlaceDonationError({placeId: place.id, error: undefined})
      }
    }
  }, [showAccessibilityMode, previousShowAccessibilityMode, error])

  const onChange = useCallback(
    newDonation => {
      if (error) {
        setPlaceDonationError({placeId: place.id, error: undefined})
      }
      addPlaceDonation({placeId: place.id, donation: newDonation})
    },
    [error, setPlaceDonationError, addPlaceDonation, place.id],
  )

  const onBlur = useCallback(() => {
    const newError = checkDonationError(ticket, donation, {
      excludeErrors: [DONATION_ERROR.EMPTY_DONATION],
    })

    if (newError) {
      setPlaceDonationError({placeId: place.id, error: newError})
    }
  }, [ticket, donation, setPlaceDonationError, place.id])

  return (
    <DonationInput
      key={`${place.id} ${showAccessibilityMode} ${inMobileBasket} ${inDescription}`} // remount on mode change to sync value
      label={wholeTable ? t('tickets.donationInputLabel.wholeTable') : undefined}
      ticket={ticket}
      onChange={onChange}
      onBlur={onBlur}
      value={donation}
      error={error}
      className={classes.root}
      describedBy={describedBy}
    />
  )
}
